/**
 * How much of an appliance's cycle is left, and how long it has been running.
 *
 * An appliance card is judged by one question a lighting card never asks:
 * *"how long until it is done?"* Three different places can answer it, and the
 * page has to read all three because the house mixes them:
 *
 *  - **the appliance's own value** — the washer, dryer, oven, microwave and
 *    coffee machine classes all publish `remaining_time` in minutes;
 *  - **a switch-off timer** — a plug that will cut its load at
 *    `switch_off_timer__scheduled_off_at`;
 *  - **a run limit** — `appliance_run_limit` counts `running_minutes` against
 *    the ceiling in its config.
 *
 * This module is the decision table those three share: it takes the values the
 * hub reports plus the device's automation configs, and answers with one
 * remaining-time reading. The components only format and draw it.
 */

export type ApplianceRuntimeSource = 'program' | 'timer' | 'run-limit';

export interface ApplianceRuntime {
  source: ApplianceRuntimeSource;
  /** Seconds left. Always > 0 — an elapsed cycle yields `null`, not zero. */
  remainingSeconds: number;
  /** What "all of it" is, when the source knows: a timer's window, a limit's
   *  ceiling, or the longest this device has reported this session. */
  totalSeconds: number | null;
  /** `remainingSeconds / totalSeconds`, clamped — the ring's sweep. */
  ratio: number | null;
  /** Compact text: `1h 12m`, `12m`, `4:32`. */
  label: string;
}

/** The automation fields this module reads (the store's summary carries them). */
export interface ApplianceAutomationLike {
  automationType?: string | null;
  config?: Record<string, unknown> | null;
}

export interface ApplianceRuntimeInput {
  values: Record<string, unknown>;
  automations?: readonly ApplianceAutomationLike[];
  /** Wall clock, injectable for tests. */
  nowMs?: number;
  /**
   * The largest remaining this device has reported this session. An appliance
   * value carries no denominator — a washer that says "42" is 42 minutes into a
   * cycle of unknown length — so the cycle's own peak stands in for it and the
   * ring fills from there.
   */
  peakSeconds?: number | null;
}

/** A number from a hub value, which may arrive as a numeric string. */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const MINUTE = 60;

/**
 * `1h 12m`, `12m`, `45s` — the card's own scale, where a seconds field on a
 * 90-minute wash is noise. Anything under a minute keeps its seconds, because
 * that is when they start to matter.
 */
export function formatRemainingLabel(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
}

/** The same reading at clock precision — a timer's `4:32`, a limit's `1:02:15`. */
export function formatRemainingClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/** The config object of the first automation of `type`, if any. */
function configOf(automations: readonly ApplianceAutomationLike[], type: string): Record<string, unknown> | null {
  const match = automations.find((automation) => automation.automationType === type);
  return match?.config ?? null;
}

function ratioOf(remainingSeconds: number, totalSeconds: number | null): number | null {
  if (totalSeconds === null || totalSeconds <= 0) return null;
  return Math.min(1, Math.max(0, remainingSeconds / totalSeconds));
}

/**
 * The appliance's own `remaining_time` — minutes, and only meaningful while the
 * machine runs. A stopped washer keeps its last "0", which is not a countdown.
 */
function programRuntime(values: Record<string, unknown>, peakSeconds: number | null): ApplianceRuntime | null {
  const minutes = toFiniteNumber(values['remaining_time']);
  if (minutes === null || minutes <= 0) return null;

  const remainingSeconds = minutes * MINUTE;
  const totalSeconds = peakSeconds !== null && peakSeconds >= remainingSeconds ? peakSeconds : remainingSeconds;

  return {
    source: 'program',
    remainingSeconds,
    totalSeconds,
    ratio: ratioOf(remainingSeconds, totalSeconds),
    label: formatRemainingLabel(remainingSeconds),
  };
}

/** A plug that will cut its load when `scheduled_off_at` arrives. */
function timerRuntime(
  values: Record<string, unknown>,
  automations: readonly ApplianceAutomationLike[],
  nowMs: number,
): ApplianceRuntime | null {
  const scheduled = values['switch_off_timer__scheduled_off_at'];
  if (typeof scheduled !== 'string' || scheduled.trim() === '') return null;

  const deadline = new Date(scheduled).getTime();
  if (!Number.isFinite(deadline)) return null;

  const remainingSeconds = (deadline - nowMs) / 1000;
  if (remainingSeconds <= 0) return null;

  const window = toFiniteNumber(configOf(automations, 'switch_off_timer')?.['on_duration_seconds']);
  const totalSeconds = window !== null && window > 0 ? window : null;

  return {
    source: 'timer',
    remainingSeconds,
    totalSeconds,
    ratio: ratioOf(remainingSeconds, totalSeconds),
    label: formatRemainingClock(remainingSeconds),
  };
}

/** A run limit counting up to its ceiling: the remainder is the answer. */
function runLimitRuntime(
  values: Record<string, unknown>,
  automations: readonly ApplianceAutomationLike[],
): ApplianceRuntime | null {
  const runningMinutes = toFiniteNumber(values['appliance_run_limit__running_minutes']);
  if (runningMinutes === null || runningMinutes <= 0) return null;

  const maxMinutes = toFiniteNumber(configOf(automations, 'appliance_run_limit')?.['max_runtime_minutes']) ?? 120;
  const remainingSeconds = (maxMinutes - runningMinutes) * MINUTE;
  if (remainingSeconds <= 0) return null;

  return {
    source: 'run-limit',
    remainingSeconds,
    totalSeconds: maxMinutes * MINUTE,
    ratio: ratioOf(remainingSeconds, maxMinutes * MINUTE),
    label: formatRemainingLabel(remainingSeconds),
  };
}

/**
 * The one reading a card shows. The appliance's own countdown wins — it is the
 * machine's own answer about its own cycle — then an armed timer, then a run
 * limit's remaining headroom.
 */
export function readApplianceRuntime(input: ApplianceRuntimeInput): ApplianceRuntime | null {
  const automations = input.automations ?? [];
  const nowMs = input.nowMs ?? Date.now();

  return (
    programRuntime(input.values, input.peakSeconds ?? null) ??
    timerRuntime(input.values, automations, nowMs) ??
    runLimitRuntime(input.values, automations)
  );
}

/** The device values a card feeds in, by key. */
export function valuesByKey(
  rows: readonly { valueKey?: string | null; currentValue?: unknown }[] | null | undefined,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const row of rows ?? []) {
    const key = String(row.valueKey ?? '').trim();
    if (key) values[key] = row.currentValue ?? null;
  }
  return values;
}

/** The value keys a load's on/off state can live under, in the order they win. */
const LOAD_STATE_KEYS = ['on_off', 'power', 'state', 'switch', 'online'] as const;

/**
 * Is the machine on, off, or does it report no such state at all?
 *
 * The third answer is the one that matters for a card's picture: a fridge, a
 * NAS volume, a drive temperature — none of them report a load, so "not running"
 * there means nothing and their photo must not be greyed (owner, 25.10.2026:
 * *"display device thumb image in grayed filter when device is off"* — grey is
 * for a device that IS off, not for one that cannot be).
 */
export function loadStateOf(values: Record<string, unknown>): 'on' | 'off' | 'unknown' {
  for (const key of LOAD_STATE_KEYS) {
    const value = values[key];
    if (value === true || value === 1 || value === 'on' || value === 'true') return 'on';
    if (value === false || value === 0 || value === 'false') return 'off';
  }
  return 'unknown';
}
